import dayjs from "dayjs";
import { ChevronDown, Search, Trash2 } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import AppAlertDialog from "@/components/Core/AppAlertDialog";
import Checkbox from "@/components/Core/Checkbox/Checkbox";
import AvailabilityHeader from "@/components/Shared/AvailabilityHeader/AvailabilityHeader";
import EmptyState from "@/components/Shared/EmptyState/EmptyState";
import LoadingIndicator from "@/components/Shared/LoadingIndicator/LoadingIndicator";
import ListViewTagChips from "@/components/Shared/TagChip/ListViewTagChips";
import GuaranteedHoursCell from "@/components/GuaranteedHours/GuaranteedHoursCell";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { APPLICATION_STATUS } from "@/configs/constants/ApplicationStatus";
import { JOB_STATUS } from "@/configs/constants/JobStatus";
import { routes } from "@/configs/constants/routes";
import useDebounce from "@/hooks/useDebounce";
import useShiftKey from "@/hooks/useShift";
import { cn } from "@/lib/utils";
import { ConsultantTag } from "@/models/Consultant";
import Job, { Application, Invitation } from "@/models/Job";
import { MatchingConsultant } from "@/models/User";
import { useUpdateConsultant } from "@/networking/consultant.hooks";
import {
	useApplications,
	useDeleteApplication,
	useDeleteInvitation,
	useInviteConsultantToJob,
	useJobShifts,
	useMatchingConsultants,
	useUpdateApplicationStatus,
} from "@/networking/job.hooks";
import { useOrganisation } from "@/networking/organisation.hooks";
import { ConfirmModalProps, useConfirmModalStore } from "@/stores/confirmModal.store";
import { weekdaysToQuery } from "@/utils/availability.utils";
import { removeConsultantTag, renewConsultantTag } from "@/utils/credential.utils";
import { formatTimestamp } from "@/utils/date.time.utils";
import { consultantRoute } from "@/utils/route.utils";
import { decimalToPercentage } from "@/utils/string.utils";
import { toast } from "@/utils/toast";
import AvailabilityStrip from "../JobConsultantPanel/AvailabilityStrip";
import MatchingFilterBar from "../JobConsultantPanel/MatchingFilterBar";
import StatusPill from "../JobConsultantPanel/StatusPill";
import {
	isEmptyCredentialFilter,
	missingCredentials,
	requirementsToCredentialFilter,
	toCompetenceFilter,
	CompetenceFilter,
	CredentialFilter,
} from "../JobConsultantPanel/jobConsultantPanel.utils";
import { ScheduleNavigationState } from "../JobMatchingTab";
import BookShiftsButton from "./BookShiftsButton";
import {
	invitedShiftTimes,
	resolveApplicationShifts,
	shiftTimeMap,
	splitInbox,
	unmannedShiftIds,
	ShiftTime,
} from "./jobCandidates.utils";

const declineButtonClasses =
	"rounded border border-solid border-border-strong bg-white px-3 py-1.5 type-label font-medium text-gray-700 hover:bg-gray-100";
const dangerButtonClasses =
	"rounded border border-solid border-border-strong bg-white px-3 py-1.5 type-label font-medium text-danger hover:bg-gray-100";

const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<h3 className="mb-3.5 flex items-center gap-2 type-label font-bold uppercase tracking-wider text-muted-foreground">
		{children}
	</h3>
);

/** Rendered size of the avatar (h-10 at xl), at 2x for retina. */
const AVATAR_IMAGE_HEIGHT = 80;

/**
 * The consultant's profile picture, falling back to their initials when there
 * is none — or when the image fails to load, so a dead asset URL leaves the
 * initials rather than a broken-image hole in the card. `image` is a full URL
 * from the asset service, which resizes via `?height=` (see media.store).
 */
const Avatar: React.FC<{ firstName?: string; lastName?: string; image?: string }> = ({
	firstName,
	lastName,
	image,
}) => {
	const [imageFailed, setImageFailed] = useState(false);
	const shellClasses = "h-9 w-9 shrink-0 rounded-full xl:h-10 xl:w-10";

	if (image && !imageFailed) {
		return (
			<img
				src={`${image}?height=${AVATAR_IMAGE_HEIGHT}`}
				// Decorative: the consultant's name is right next to it as a link
				alt=""
				onError={() => setImageFailed(true)}
				className={cn(shellClasses, "border border-solid border-border object-cover")}
			/>
		);
	}

	return (
		<span
			className={cn(
				shellClasses,
				"flex items-center justify-center bg-gray-100 type-label font-semibold text-gray-700"
			)}
		>
			{(firstName?.[0] ?? "") + (lastName?.[0] ?? "")}
		</span>
	);
};

/** How many shift chips to show before collapsing behind a "show all" toggle. */
const SHIFT_CHIP_CAP = 5;

/**
 * The applied/booked shifts of one inbox card as date/time chips — capped at
 * {@link SHIFT_CHIP_CAP} with a toggle to expand the rest (a job can carry
 * dozens of shifts). `leadLabel` prefixes the row (e.g. "Bokas på:").
 */
const ApplicationShiftChips: React.FC<{ shifts: ShiftTime[]; leadLabel?: string }> = ({
	shifts,
	leadLabel,
}) => {
	const { t } = useTranslation();
	const [expanded, setExpanded] = useState(false);

	if (!shifts.length) return null;

	const shown = expanded ? shifts : shifts.slice(0, SHIFT_CHIP_CAP);
	const hiddenCount = shifts.length - shown.length;

	return (
		<div className="mt-1.5 flex flex-wrap items-center gap-1">
			{leadLabel && (
				<span className="mr-0.5 type-label font-semibold text-muted-foreground">{leadLabel}:</span>
			)}
			{shown.map((shift) => (
				<span
					key={shift.id}
					className="inline-flex items-center rounded border border-solid border-border-strong bg-gray-100 px-2 py-0.5 type-label tabular-nums text-gray-800"
				>
					{dayjs(shift.start).format("ddd D/M")} {dayjs(shift.start).format("HH:mm")}–
					{dayjs(shift.end).format("HH:mm")}
				</span>
			))}
			{(hiddenCount > 0 || expanded) && (
				<button
					type="button"
					onClick={() => setExpanded((prev) => !prev)}
					className="border-0 bg-transparent p-0 type-label font-medium text-action hover:underline"
				>
					{expanded
						? t("jobCandidates.showFewerShifts")
						: t("jobCandidates.showAllShifts", { count: shifts.length })}
				</button>
			)}
		</div>
	);
};

/**
 * The "Kandidater" tab of the job details page — people and decisions, while
 * the Schema tab owns time and placement:
 *
 * - An inbox of applications and invitations ("önskningar") with INSTANT
 *   whole-job actions (Boka/Neka/Avboka/ta bort önskan) — these answer the
 *   consultant and deliberately bypass the Schema tab's draft model.
 * - A consultant search on the same requirement filter as the consultant
 *   panel, with the vetting columns the schedule grid can't show
 *   (availability, guaranteed hours, TG) and the bulk actions (invite many,
 *   toggle tags). Proactive booking routes INTO the Schema tab's picker so all
 *   placement goes through one draft/save model.
 */
const JobCandidatesTab: React.FC<{ job: Job }> = ({ job }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const modalStore = useConfirmModalStore();
	const { data: organisation } = useOrganisation();

	const { mutate: updateApplicationStatus } = useUpdateApplicationStatus();
	const { mutate: deleteInvitation } = useDeleteInvitation();
	const { mutate: deleteApplication } = useDeleteApplication();
	// mutateAsync, not mutate: the bulk invite loop needs to await each call and
	// see rejections — `mutate` returns void, so it can neither be waited on nor
	// caught
	const { mutateAsync: inviteConsultant } = useInviteConsultantToJob();
	const { mutateAsync: updateConsultant } = useUpdateConsultant();

	// Advanced, deliberately hidden: holding Shift reveals a delete action on
	// archived rows that fully removes the application so the consultant can
	// re-apply (a declined application otherwise blocks a new one).
	const [shiftHeld, setShiftHeld] = useState(false);
	useShiftKey(setShiftHeld);
	// The archived application queued for hard-delete (drives the confirm dialog).
	const [pendingDelete, setPendingDelete] = useState<Application | null>(null);

	// ---------------------------------------------------------------- inbox

	const {
		applications,
		invitations,
		wouldBookShifts,
		isLoading: isLoadingInbox,
	} = useApplications({
		jobId: job.id,
		includeInvitations: true,
	});

	const inbox = useMemo(() => splitInbox(applications, invitations), [applications, invitations]);
	const pendingCount = inbox.pendingApplications.length + inbox.openInvitations.length;
	const archivedCount = inbox.archived.applications.length + inbox.archived.invitations.length;
	const [showArchived, setShowArchived] = useState(false);

	// Job shifts resolve the applications' snapshotted shift ids (the endpoint
	// returns ids only) to real times, and tell us which are still unmanned —
	// so the inbox can show exactly which shifts accepting will book.
	const { data: jobShifts } = useJobShifts({
		jobId: job.id,
		status: ["NOT_SET", "EXTEND_REQUESTED", "EXTEND_DECLINED", "EXTEND_ACCEPTED"],
		include: ["applications"],
	});
	const shiftById = useMemo(() => shiftTimeMap(jobShifts), [jobShifts]);
	const unmannedIds = useMemo(() => unmannedShiftIds(jobShifts), [jobShifts]);

	// The still-unmanned shifts with times — what the direct-booking picker
	// lists (only available shifts are bookable)
	const availableShifts = useMemo(
		() =>
			[...unmannedIds]
				.map((id) => shiftById.get(id))
				.filter((shift): shift is ShiftTime => !!shift),
		[unmannedIds, shiftById]
	);

	// Booking straight from this tab mirrors the accept rule: the job must
	// still have bookable shifts and not be closed/cancelled
	const jobIsBookable =
		(job.status === JOB_STATUS.OPEN || job.status === JOB_STATUS.BOOKED_WITH_UNMANNED_SHIFTS) &&
		availableShifts.length > 0;

	const hasInboxItems = pendingCount + archivedCount + inbox.acceptedApplications.length > 0;

	function declineApplication(application: Application) {
		updateApplicationStatus({
			jobId: job.id,
			applicationId: application.id,
			status: APPLICATION_STATUS.DECLINED,
			// Declining a booked application unbooks the consultant; a pending one
			// is simply denied — the toast wording differs.
			wasBooked: application.status === APPLICATION_STATUS.ACCEPTED,
		});
	}

	function confirmDeleteInvitation(invitationId: string) {
		const confirmModal: ConfirmModalProps = {
			title: "DELETE_INVITATION",
			text: "DELETE_INVITATION_CONFIRM",
			acceptButtonText: "YES",
			rejectButtonText: "NO",
			onAccept: () => deleteInvitation({ jobId: job.id, invitationId }),
			onReject: () => {},
			type: "DANGER",
		};
		modalStore.newModal(confirmModal);
	}

	/** Open the Schema tab scrolled to the application's first shift. */
	function showInSchedule(shifts: ShiftTime[]) {
		const firstStart = shifts[0]?.start;
		const state: ScheduleNavigationState = {
			scrollToDate: firstStart ? dayjs(firstStart).format("YYYY-MM-DD") : undefined,
		};
		navigate(routes.JOB_SCHEDULE, { state });
	}

	// --------------------------------------------------------------- search

	const [searchTerm, setSearchTerm] = useState("");
	const debouncedSearchTerm = useDebounce(searchTerm, 500);
	const [filterCustomerExperience, setFilterCustomerExperience] = useState(false);
	const [filterShowBooked, setFilterShowBooked] = useState(false);
	const [filterInvited, setFilterInvited] = useState(false);
	const [filteredDays, setFilteredDays] = useState<number[]>([]);
	const [selectedRows, setSelectedRows] = useState(new Map<string, MatchingConsultant>());

	const jobRequirementsFilter = useMemo(
		() => requirementsToCredentialFilter(job.requirements),
		[job.requirements]
	);
	const [credentialFilter, setCredentialFilter] = useState<CredentialFilter>(jobRequirementsFilter);
	const jobCompetenceFilter = useMemo(() => toCompetenceFilter(job.competence), [job.competence]);
	const [competenceFilter, setCompetenceFilter] = useState<CompetenceFilter | undefined>(
		jobCompetenceFilter
	);

	// The hook sets `initialData: []`, so the query is never in `isLoading` —
	// `isFetching` is the only signal that a load is in flight.
	const {
		data: consultants,
		isFetching,
		refetch,
	} = useMatchingConsultants({
		jobId: job.id,
		availability: organisation ? weekdaysToQuery(filteredDays) : undefined,
		branch: competenceFilter?.branch ?? "any",
		service: competenceFilter?.service ?? "any",
		skills: competenceFilter?.skills.length ? competenceFilter.skills.join(",") : undefined,
		hasCustomerExperience: filterCustomerExperience + "",
		includeConsultantsWithOverlappedJob: filterShowBooked + "",
		isInvited: filterInvited + "",
		keyword: debouncedSearchTerm.trim(),
		pageSize: 300,
		expand: "guaranteedHours",
	});

	// Only consultants passing the credential filter are listed — relax the
	// filter chips to widen the match.
	const matching = useMemo(
		() =>
			(consultants || []).filter((consultant) =>
				isEmptyCredentialFilter(missingCredentials(consultant, credentialFilter))
			),
		[consultants, credentialFilter]
	);

	function toggleRow(selected: boolean, consultant: MatchingConsultant) {
		setSelectedRows((prev) => {
			const next = new Map(prev);
			if (selected) next.set(consultant.id, consultant);
			else next.delete(consultant.id);
			return next;
		});
	}

	function toggleAvailabilityDay(day: number) {
		setFilteredDays((prev) =>
			prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
		);
	}

	async function inviteSelectedConsultants() {
		const consultants = Array.from(selectedRows.values());
		const toastId = toast.loading(t("job.sendingInvitations"));

		// Sequential but settled: one rejection must not skip the consultants
		// queued behind it, so failures are collected and reported together.
		// `silent` suppresses the hook's own per-invite toasts.
		const failed: MatchingConsultant[] = [];

		for (const consultant of consultants) {
			try {
				await inviteConsultant({ jobId: job.id, consultantId: consultant.id, silent: true });
			} catch (e) {
				failed.push(consultant);
			}
		}

		if (!failed.length) {
			toast.success(t("job.invitationsSent"), { id: toastId });
			setSelectedRows(new Map());
			return;
		}

		// Keep just the failures selected so they can be retried directly
		setSelectedRows(new Map(failed.map((consultant) => [consultant.id, consultant])));

		if (failed.length === consultants.length) {
			toast.error(t("job.invitationsFailed"), { id: toastId });
		} else {
			toast.warning(
				t("job.invitationsPartiallyFailed", {
					failed: failed.length,
					total: consultants.length,
				}),
				{ id: toastId }
			);
		}
	}

	/**
	 * Bulk-assign a consultant tag to every selected consultant. Consultants who
	 * already hold it get a fresh entry, so a timed tag's clock restarts —
	 * re-marking never removes. Removal is {@link removeTagFromSelectedConsultants}.
	 */
	async function assignTagToSelectedConsultants(tagKey: string) {
		const tagType = organisation?.consultantTagTypes?.find((type) => type.key === tagKey);
		if (!tagType) return;

		for (const consultant of Array.from(selectedRows.values())) {
			await updateConsultant({
				userId: consultant.id,
				update: { profile: { tags: renewConsultantTag(consultant.profile?.tags ?? [], tagType) } },
				silent: true,
			});
		}

		setSelectedRows(new Map());
		refetch();
	}

	/** Bulk-remove a consultant tag from every selected consultant that holds it. */
	async function removeTagFromSelectedConsultants(tagKey: string) {
		const holders = Array.from(selectedRows.values()).filter((consultant) =>
			(consultant.profile?.tags ?? []).some((tag: ConsultantTag) => tag.key === tagKey)
		);

		for (const consultant of holders) {
			await updateConsultant({
				userId: consultant.id,
				update: { profile: { tags: removeConsultantTag(consultant.profile?.tags ?? [], tagKey) } },
				silent: true,
			});
		}

		setSelectedRows(new Map());
		refetch();
	}

	// -------------------------------------------------------------- render

	function renderApplicationCard(application: Application, accepted: boolean) {
		const { consultant } = application;
		// For a pending application, show what accepting would actually book: the
		// server's would-book preview (still-unmanned shifts, one per time window
		// — applications are job-level, the booking is allocated at accept time).
		// Falls back to the application's own shifts filtered to unmanned until
		// the backend serves the preview. A booked application shows the shifts
		// it is booked on.
		const shifts = accepted
			? resolveApplicationShifts(application, shiftById)
			: wouldBookShifts ?? resolveApplicationShifts(application, shiftById, unmannedIds);
		const fullyStaffed = !accepted && shifts.length === 0;
		return (
			<div
				key={application.id}
				className={cn(
					"flex items-center gap-3 rounded-lg border border-solid border-border border-l-[3px] bg-white px-4 py-3 xl:gap-4 xl:px-5 xl:py-3.5",
					accepted ? "border-l-success/60" : "border-l-success"
				)}
			>
				<Avatar
					firstName={consultant.firstName}
					lastName={consultant.lastName}
					image={consultant.profile?.profilePicture}
				/>
				<div className="min-w-0">
					<div className="flex flex-wrap items-center gap-1.5">
						<Link to={consultantRoute(consultant.id)} target="_blank">
							<span
								data-contact={consultant.id}
								className="type-name font-semibold text-action underline underline-offset-2"
							>
								{consultant.firstName} {consultant.lastName}
							</span>
						</Link>
						{accepted && <StatusPill color="green">{t("job.jobStatus.booked")}</StatusPill>}
						{consultant.hasCustomerExperience && (
							<StatusPill color="orange">{t("job.customerExperienceShort")}</StatusPill>
						)}
						<ListViewTagChips
							certifications={consultant.profile?.certifications}
							certificationTypes={organisation?.certificationTypes}
						/>
					</div>
					<p className="m-0 mt-0.5 type-meta text-muted-foreground">
						{t("jobCandidates.appliedAt", {
							date: application.metadata ? formatTimestamp(application.metadata.created) : "–",
						})}
						{consultant.profile?.address?.city && ` · ${consultant.profile.address.city}`}
					</p>
					<ApplicationShiftChips
						shifts={shifts}
						leadLabel={accepted ? undefined : (t("jobCandidates.willBook") as string)}
					/>
					{fullyStaffed && (
						<p className="m-0 mt-1.5 type-meta text-muted-foreground">
							{t("jobCandidates.fullyStaffed")}
						</p>
					)}
				</div>
				<div className="ml-auto flex shrink-0 items-center gap-2">
					{shifts.length > 0 && (
						<button
							type="button"
							onClick={() => showInSchedule(shifts)}
							className="border-0 bg-transparent p-0 type-label font-medium text-action hover:underline"
						>
							{t("jobCandidates.showInSchedule")}
						</button>
					)}
					{!accepted && (
						<>
							<button
								type="button"
								onClick={() => declineApplication(application)}
								className={declineButtonClasses}
							>
								{t("job.action.decline")}
							</button>
							{/* Booking a fully staffed job would only fail server-side
							    ("no unmanned shifts left") — don't offer it. The split
							    button books via POST /book (which consumes the pending
							    application); the default selection is the would-book
							    preview shown in the chips, the chevron tweaks it. */}
							{jobIsBookable && !fullyStaffed && (
								<BookShiftsButton
									jobId={job.id}
									consultantId={consultant.id}
									consultantName={`${consultant.firstName} ${consultant.lastName}`}
									availableShifts={availableShifts}
									defaultShiftIds={shifts.map((shift) => shift.id)}
								/>
							)}
						</>
					)}
					{accepted && (
						<button
							type="button"
							onClick={() => declineApplication(application)}
							className={dangerButtonClasses}
						>
							{t("job.action.cancelApplication")}
						</button>
					)}
				</div>
			</div>
		);
	}

	function renderInvitationCard(invitation: Invitation) {
		const { consultant } = invitation;
		// Invitations are stored per shift — resolve which shifts this
		// consultant is asked for, like the application cards' chips
		const shifts = invitedShiftTimes(jobShifts, consultant.id);
		return (
			<div
				key={invitation.id}
				className="flex items-center gap-3 rounded-lg border border-solid border-border border-l-[3px] border-l-orange-400 bg-white px-4 py-3 xl:gap-4 xl:px-5 xl:py-3.5"
			>
				<Avatar
					firstName={consultant.firstName}
					lastName={consultant.lastName}
					image={consultant.profile?.profilePicture}
				/>
				<div className="min-w-0">
					<div className="flex flex-wrap items-center gap-1.5">
						<Link to={consultantRoute(consultant.id)} target="_blank">
							<span
								data-contact={consultant.id}
								className="type-name font-semibold text-action underline underline-offset-2"
							>
								{consultant.firstName} {consultant.lastName}
							</span>
						</Link>
						<StatusPill color="orange">{t("job.invited")}</StatusPill>
						<ListViewTagChips
							certifications={consultant.profile?.certifications}
							certificationTypes={organisation?.certificationTypes}
						/>
					</div>
					<p className="m-0 mt-0.5 type-meta text-muted-foreground">
						{t("jobCandidates.invitedAt", {
							date: invitation.metadata ? formatTimestamp(invitation.metadata.created) : "–",
						})}
					</p>
					<ApplicationShiftChips
						shifts={shifts}
						leadLabel={t("jobCandidates.invitedFor") as string}
					/>
				</div>
				<div className="ml-auto flex shrink-0 items-center gap-2">
					{shifts.length > 0 && (
						<button
							type="button"
							onClick={() => showInSchedule(shifts)}
							className="border-0 bg-transparent p-0 type-label font-medium text-action hover:underline"
						>
							{t("jobCandidates.showInSchedule")}
						</button>
					)}
					<button
						type="button"
						onClick={() => confirmDeleteInvitation(invitation.id)}
						className={dangerButtonClasses}
					>
						{t("consultantPanel.uninvite")}
					</button>
					{/* Book the invited consultant without waiting for their answer —
					    the backend consumes the invitation as part of the booking.
					    Defaults to the INVITED shifts (that's what they were asked
					    for); the picker still offers everything available. */}
					{jobIsBookable && (
						<BookShiftsButton
							jobId={job.id}
							consultantId={consultant.id}
							consultantName={`${consultant.firstName} ${consultant.lastName}`}
							availableShifts={availableShifts}
							defaultShiftIds={shifts.map((shift) => shift.id)}
						/>
					)}
				</div>
			</div>
		);
	}

	function archivedApplicationLabel(application: Application): string {
		if (application.status !== APPLICATION_STATUS.DECLINED) return t("job.jobStatus.cancelled");
		// DECLINED is overloaded: a booked application that was later unbooked
		// carries an acceptedAt; a pending one that was just denied never was.
		return application.metadata?.acceptedAt
			? t("job.jobStatus.applicationDeclined")
			: t("job.jobStatus.applicationDenied");
	}

	function renderArchivedRow(item: { application?: Application; invitation?: Invitation }) {
		const consultant = (item.application ?? item.invitation)!.consultant;
		const status = item.application
			? archivedApplicationLabel(item.application)
			: t(`job.invitationStatus.${item.invitation!.status!.toLowerCase()}` as any);
		const created = (item.application ?? item.invitation)?.metadata?.created;
		// The shifts this application was for (resolved to times, no unmanned
		// filter — it's history, show exactly what they applied for).
		const shifts = item.application ? resolveApplicationShifts(item.application, shiftById) : [];
		return (
			<div
				key={(item.application ?? item.invitation)!.id}
				// No `opacity` here: it dims the link colour and the status pill
				// along with the text, which is exactly the washed-out look users
				// complained about. The muted text tier carries "this is history".
				className="flex items-start gap-2 rounded-md border border-solid border-border bg-white px-4 py-2"
			>
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center gap-2">
						<Link to={consultantRoute(consultant.id)} target="_blank">
							<span className="type-meta font-medium text-action underline underline-offset-2">
								{consultant.firstName} {consultant.lastName}
							</span>
						</Link>
						<StatusPill color="gray">{status}</StatusPill>
						{created && (
							<span className="type-label text-foreground-subtle">{formatTimestamp(created)}</span>
						)}
					</div>
					<ApplicationShiftChips shifts={shifts} />
				</div>
				{/* Hidden advanced action: Shift reveals a hard-delete so the
				    consultant can re-apply (declined applications block re-apply) */}
				{shiftHeld && item.application && (
					<button
						type="button"
						onClick={() => setPendingDelete(item.application!)}
						title={t("jobCandidates.deleteApplicationHint") as string}
						className="flex shrink-0 items-center gap-1 rounded border border-solid border-red-300 bg-white px-2 py-1 type-label font-medium text-red-700 hover:bg-red-50"
					>
						<Trash2 size={12} />
						{t("jobCandidates.deleteApplication")}
					</button>
				)}
			</div>
		);
	}

	function renderConsultantRow(consultant: MatchingConsultant) {
		const lowTg = consultant.tg != null && consultant.tg < 0.3;
		return (
			<tr
				key={consultant.id}
				data-contact={consultant.id}
				className={cn(
					"border-b border-border last:border-b-0 hover:bg-gray-100",
					selectedRows.has(consultant.id) && "bg-action/5",
					consultant.blocked && "bg-red-50"
				)}
			>
				<td className="py-2.5 pl-4 pr-1 xl:py-3">
					<input
						type="checkbox"
						disabled={consultant.blocked}
						checked={selectedRows.has(consultant.id)}
						onChange={(e) => toggleRow(e.target.checked, consultant)}
					/>
				</td>
				<td className="px-3 py-2.5 xl:py-3">
					<div className="flex flex-wrap items-center gap-1.5">
						<Link to={consultantRoute(consultant.id)} target="_blank">
							<span className="type-body font-medium text-action underline underline-offset-2">
								{consultant.firstName} {consultant.lastName}
							</span>
						</Link>
						{consultant.blocked && <StatusPill color="red">Blockerad</StatusPill>}
						{consultant.hasCustomerExperience && (
							<StatusPill color="orange">{t("job.customerExperienceShort")}</StatusPill>
						)}
						<ListViewTagChips
							tags={consultant.profile?.tags}
							tagTypes={organisation?.consultantTagTypes}
							certifications={consultant.profile?.certifications}
							certificationTypes={organisation?.certificationTypes}
						/>
					</div>
					{consultant.profile?.internalNotes && (
						<p className="m-0 mt-0.5 max-w-[260px] truncate type-label font-medium text-muted-foreground xl:max-w-[400px]">
							{consultant.profile.internalNotes}
						</p>
					)}
				</td>
				<td className="px-3 py-2.5 type-body text-gray-800 xl:py-3">
					{consultant.profile?.address?.city || "–"}
				</td>
				<td className="px-3 py-2.5 xl:py-3">
					<AvailabilityStrip
						availability={consultant.profile?.availability}
						organisation={organisation}
					/>
				</td>
				<td className="px-3 py-2.5 xl:py-3">
					<GuaranteedHoursCell guaranteedHours={consultant.guaranteedHours} />
				</td>
				<td
					className={cn(
						"px-3 py-2.5 type-body font-medium tabular-nums xl:py-3",
						lowTg ? "text-red-700" : "text-gray-800"
					)}
				>
					{decimalToPercentage(consultant.tg)}
				</td>
				<td className="px-3 py-2.5 type-body text-gray-800 xl:py-3">
					{consultant.isInvited ? t("YES") : "–"}
				</td>
				<td className="py-2.5 pl-3 pr-4 text-right xl:py-3">
					{!consultant.blocked && jobIsBookable && (
						<BookShiftsButton
							jobId={job.id}
							consultantId={consultant.id}
							consultantName={`${consultant.firstName} ${consultant.lastName}`}
							availableShifts={availableShifts}
						/>
					)}
				</td>
			</tr>
		);
	}

	// The width cap grows on 2xl so the larger type gets room instead of wrapping
	return (
		<div className="mx-auto mb-8 mt-4 flex max-w-[1500px] flex-col gap-11 2xl:max-w-[1720px] 2xl:gap-12">
			{/* -------- Applications & invitations inbox: instant whole-job answers */}
			<section>
				<SectionHeader>
					{t("jobCandidates.inboxTitle")}
					{pendingCount > 0 && (
						<span className="font-medium normal-case tracking-normal text-foreground-subtle">
							{t("jobCandidates.pendingCount", { count: pendingCount })}
						</span>
					)}
				</SectionHeader>

				{!hasInboxItems && <EmptyState label="NO_APPLICATION_MESSAGE" isLoading={isLoadingInbox} />}

				<div className="flex flex-col gap-2">
					{inbox.pendingApplications.map((application) =>
						renderApplicationCard(application, false)
					)}
					{inbox.openInvitations.map(renderInvitationCard)}
					{inbox.acceptedApplications.map((application) =>
						renderApplicationCard(application, true)
					)}

					{archivedCount > 0 && (
						<>
							<button
								type="button"
								onClick={() => setShowArchived((prev) => !prev)}
								className="flex items-center gap-1 self-start border-0 bg-transparent p-0 py-1 type-body font-medium text-muted-foreground hover:text-gray-900"
							>
								{t("jobCandidates.earlier", { count: archivedCount })}
								<ChevronDown
									className={cn("h-4 w-4 transition-transform", showArchived && "rotate-180")}
								/>
							</button>
							{showArchived && (
								<>
									{inbox.archived.applications.map((application) =>
										renderArchivedRow({ application })
									)}
									{inbox.archived.invitations.map((invitation) =>
										renderArchivedRow({ invitation })
									)}
								</>
							)}
						</>
					)}
				</div>
			</section>

			{/* -------- Consultant search: vetting, bulk actions, booking via Schema */}
			<section>
				<SectionHeader>{t("jobCandidates.searchTitle")}</SectionHeader>

				<div className="flex flex-col gap-2.5 rounded-t-lg border border-b-0 border-solid border-border bg-white px-4 py-3">
					<div className="flex flex-wrap items-center gap-4">
						<div className="relative min-w-[220px] flex-1">
							<Search
								size={14}
								className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-subtle"
							/>
							<input
								type="text"
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								placeholder={t("SEARCH") as string}
								className="w-full max-w-[280px] rounded-md border border-solid border-border-strong bg-white py-1.5 pl-8 pr-2.5 type-body text-gray-900 outline-none focus:border-brand xl:max-w-[340px] xl:py-2"
							/>
						</div>
						<Checkbox
							customStyle
							labelClassName="type-body"
							label={t("SHOW_BOOKED_CONSULTANTS")}
							isChecked={filterShowBooked}
							onChange={(e: any) => setFilterShowBooked(e.target.checked)}
						/>
						<Checkbox
							customStyle
							labelClassName="type-body"
							label={t("job.customerExperience")}
							isChecked={filterCustomerExperience}
							onChange={(e: any) => setFilterCustomerExperience(e.target.checked)}
						/>
						<Checkbox
							customStyle
							labelClassName="type-body"
							label={t("INVITED")}
							isChecked={filterInvited}
							onChange={(e: any) => setFilterInvited(e.target.checked)}
						/>
					</div>

					<MatchingFilterBar
						organisation={organisation}
						competenceFilter={competenceFilter}
						onCompetenceFilterChange={setCompetenceFilter}
						credentialFilter={credentialFilter}
						onCredentialFilterChange={setCredentialFilter}
						jobCompetenceFilter={jobCompetenceFilter}
						jobRequirementsFilter={jobRequirementsFilter}
					/>

					{selectedRows.size > 0 && (
						<div className="flex flex-wrap items-center gap-3 rounded-md border border-solid border-action/40 bg-action/5 px-3 py-1.5">
							<span className="type-body font-semibold text-gray-900">
								{t("timeReports.selectedRows", { count: selectedRows.size })}:
							</span>
							{jobIsBookable && (
								<button
									type="button"
									onClick={inviteSelectedConsultants}
									className="border-0 bg-transparent p-0 type-body font-medium text-action hover:underline"
								>
									{t("job.invite")}
								</button>
							)}
							{(organisation?.consultantTagTypes?.length ?? 0) > 0 && (
								/* Assign (or renew — timed tags restart their clock) a tag for
								   the whole selection, and a separate explicit remove. */
								<>
									<Select value="" onValueChange={assignTagToSelectedConsultants}>
										<SelectTrigger className="h-8 w-auto gap-1.5 type-body">
											<SelectValue placeholder={t("jobCandidates.assignTag")} />
										</SelectTrigger>
										<SelectContent>
											{organisation?.consultantTagTypes?.map((type) => (
												<SelectItem key={type.key} value={type.key} className="type-body">
													{type.validityDuration
														? `${type.name.sv} (${type.validityDuration} h)`
														: type.name.sv}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<Select value="" onValueChange={removeTagFromSelectedConsultants}>
										<SelectTrigger className="h-8 w-auto gap-1.5 type-body">
											<SelectValue placeholder={t("jobCandidates.removeTag")} />
										</SelectTrigger>
										<SelectContent>
											{organisation?.consultantTagTypes?.map((type) => (
												<SelectItem key={type.key} value={type.key} className="type-body">
													{type.name.sv}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</>
							)}
						</div>
					)}
				</div>

				<div className="overflow-x-auto rounded-b-lg border border-solid border-border bg-white">
					{/* table-fixed + explicit column widths: with auto layout the columns
					    are content-sized, so the header row visibly jumps to new widths
					    the moment the consultant rows load. Only NAMN is left unsized —
					    it absorbs the remaining space. */}
					<table className="w-full min-w-[1000px] table-fixed border-collapse">
						<thead>
							<tr className="border-b border-border-strong bg-gray-50 text-left type-label font-bold uppercase tracking-wide text-muted-foreground">
								<th className="w-10 py-2.5 pl-4 pr-1"></th>
								<th className="px-3 py-2.5">{t("NAME")}</th>
								<th className="w-36 px-3 py-2.5 xl:w-40">{t("consultant.address.city")}</th>
								<th className="w-52 px-3 py-2.5 xl:w-56">
									<AvailabilityHeader onClick={toggleAvailabilityDay} selectedDays={filteredDays} />
								</th>
								<th className="w-28 px-3 py-2.5">{t("jobCandidates.guaranteedHours")}</th>
								<th className="w-16 px-3 py-2.5 xl:w-20">TG</th>
								<th className="w-24 px-3 py-2.5">{t("INVITED")}</th>
								<th className="w-44 py-2.5 pl-3 pr-4 xl:w-48"></th>
							</tr>
						</thead>
						<tbody>{matching.map((consultant) => renderConsultantRow(consultant))}</tbody>
					</table>

					{/* Existing rows stay visible during refetches; the spinner only
					    covers loads with nothing to show yet. */}
					{isFetching && matching.length === 0 && (
						<div className="p-8">
							<LoadingIndicator size="s" showText={false} />
						</div>
					)}
					{!isFetching && matching.length === 0 && <EmptyState label="NO_CONSULTANTS_MESSAGE" />}
				</div>
			</section>

			<AppAlertDialog
				open={!!pendingDelete}
				onOpenChange={(open) => !open && setPendingDelete(null)}
				variant="danger"
				title="jobCandidates.deleteApplicationTitle"
				description="jobCandidates.deleteApplicationConfirm"
				confirmLabel="jobCandidates.deleteApplication"
				onConfirm={() => {
					if (pendingDelete) deleteApplication({ jobId: job.id, applicationId: pendingDelete.id });
				}}
			/>
		</div>
	);
};

export default JobCandidatesTab;
